import { useQueryClient } from '@tanstack/react-query';
import React, { Dispatch, FC, PropsWithChildren, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useIsDevEnv } from '../useIsDevEnv';

interface IDevToolsContext {
    impersonateEmail: string;
    setImpersonateEmail: Dispatch<SetStateAction<string>>;
    isImpersonating: boolean;
    setIsImpersonating: Dispatch<SetStateAction<boolean>>;
    impersonateRoles: string[];
    setImpersonateRoles: Dispatch<SetStateAction<string[]>>;
}

export const DevToolsContext = React.createContext<IDevToolsContext>({
    impersonateEmail: '',
    // biome-ignore lint/suspicious/noEmptyBlockStatements: placeholder
    setImpersonateEmail: () => {},
    isImpersonating: false,
    // biome-ignore lint/suspicious/noEmptyBlockStatements: placeholder
    setIsImpersonating: () => {},
    impersonateRoles: [],
    // biome-ignore lint/suspicious/noEmptyBlockStatements: placeholder
    setImpersonateRoles: () => {},
});

export const DevToolsProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
    const [impersonateEmail, setImpersonateEmail] = useState<string>('');
    const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
    const [impersonateRoles, setImpersonateRoles] = useState<string[]>(['default']);
    const isDevEnv = useIsDevEnv();
    const queryClient = useQueryClient();

    useEffect(() => {
        // Make testing easier by allowing direct access to the devtools api from code,
        // but only in dev env. This helps to make the E2E tests more reliable and faster.
        if (isDevEnv) {
            // biome-ignore lint/suspicious/noExplicitAny: we need to use any
            (window as any)['__DevToolsApi__'] = {
                setImpersonateEmail,
                setIsImpersonating,
            };
        } else {
            // biome-ignore lint/suspicious/noExplicitAny: we need to use any
            (window as any)['__DevToolsApi__'] = undefined;
        }
    }, [isDevEnv]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Should clear cache when changing impersonation state
    useEffect(() => {
        queryClient.clear();
    }, [isImpersonating, queryClient]);

    const contextValue = useMemo(
        () => ({
            impersonateEmail,
            setImpersonateEmail,
            isImpersonating,
            setIsImpersonating,
            setImpersonateRoles,
            impersonateRoles,
        }),
        [impersonateEmail, isImpersonating, impersonateRoles]
    );

    return <DevToolsContext.Provider value={contextValue}>{children}</DevToolsContext.Provider>;
};
